<template>
    <el-select 
        ref="selectRef"
        v-model="activeValue" 
        :placeholder="placeholder" 
        :value-key="valueKey"
        :multiple="multiple"
        :popper-class="popperClass"
        :no-data-text="$t('common.暂无数据')"
        v-bind="$attrs" 
        @change="onChange"
    >
        <!-- 值数组 -->
        <template v-if="isValue">
            <el-option
                v-if="showAllOption"
                class="Disp_AC"
                :label="$t('schedule.全部')"
                value="all"
                @click.native.stop="handleCheckAll"
            ></el-option>
            <el-option 
                class="Disp_AC"
                v-for="(item, index) in options" 
                :label="item" 
                :value="item" 
                :key="index" 
                :disabled="item.disabled"
            ></el-option>
        </template>

        <!-- 对象数组 -->
        <template v-else>
            <el-option
                v-if="showAllOption"
                class="Disp_AC"
                :value="allLabelOption"
                :label="$t('schedule.全部')"
                @click.native.stop="handleCheckAll"
            ></el-option>
            <el-option
                class="Disp_AC"
                v-for="item in options" 
                :label="item[option.label + $lang] || item[option.label]" 
                :value="valueKey === 'value' ? item[option.value] : item" 
                :key="item[option.value]" 
                :disabled="item.disabled"
            ></el-option>
        </template>
    </el-select>
</template>

<script>
	/**
     * ComponentName: cl-el-select
     * Author: zhangjun
     * 组件说明：element的下拉框组件代码量较多，简单封装下，多选状态下可以加checkbox和全选。
     * eg:
     *   <cl-el-select
            class="No_Border"
            style="width: 188px;"
            :options="templateList"
            v-model="activeTemplate"
            @change="onTemplateChange"
        ></cl-el-select>
     */
export default {
    name: 'cl-el-select',
    props: {
        // 数组项
        options: {
            type: Array,
            required: true,
        },
        // 是否是值数组，默认为对象数组
        isValue: {
            type: Boolean,
            default: false,
        },
        // 显示的文本 | 文本的值
        option: {
            type: Object,
            default: () => ({
                label: 'label',
                value: 'value',
            }),
        },
        // v-model绑定的值
        value: {
            type: [String, Number, Array, Object],
            default: '',
        },
		// 绑定的值类型
		valueKey: {
			type: String,
			default: 'value'
		},
        // 占位符
        placeholder: {
            type: String,
            default: function () {
				return this.$t('common.请选择')
			},
        },
        // 多选情况下，是否显示多选框
        showCheckbox: {
            type: Boolean,
            default: false,
        },
        // 是否多选
        multiple: {
            type: Boolean,
            default: false,
        },
        // 是否显示全选
        showSelectAll: {
            type: Boolean,
            default: false,
        }
    },

    data() {
        return {
            description: 'element的el-select组件',
            activeValue: this.multiple ? [] : '',
            checkAll: false, // 全选状态
        }
    },

    watch: {
        value: {
            immediate: true,
            handler(newV) {
                if (this.multiple) {
                    this.checkAll = newV.length > 0 && newV.length === this.options.length
                    if (this.checkAll) { // 如果值全部被选上，则unshift全选
                        this.activeValue = [this.allLabelOption, ...newV]
                    } else {
                        this.activeValue = [...newV]
                    }
                } else {
                    let val = typeof newV === 'number' ? newV.toString() : newV
                    this.activeValue = val ? val : undefined
                }
            }
        },
    },

    computed: {
        // valueKey为value时，值是基本类型，是id之类的key时，值是对象类型。
        allLabelOption() {
            return this.valueKey === 'value' ? 'all' : { [this.valueKey]: 'all' }
        },
        
        // 多选 && 是否显示全选 && 有值
        showAllOption() {
            return this.multiple && this.showSelectAll && this.options.length
        },
        
        // 下拉列表wrapper的class
        popperClass() {
            let classStr = ''
            if (this.multiple && this.showCheckbox) {
                classStr += ' Multiple_select_with_checkbox' // 复选框样式
            }
            if (this.showAllOption) {
                classStr += ' Multiple_select_with_select_all' // 全选下边框样式
            }
            return classStr
        },

    },

    created() {
        this.checkProps();
    },

    mounted() {
        this.handleRepeatText();
    },

    methods: {
        checkProps() {
            if (this.isValue && this.valueKey !== 'value') {
                throw '数组为非对象数组的情况下，valueKey的porps值必须为value。'
            }
        },
        
        // 如果是多选，input里不展示原来的标签，替换成选择的option数量。
        handleRepeatText() {
            if (this.multiple) {
                this.$nextTick(() => {
                    const dom = this.$refs.selectRef.$el.querySelector('.el-select__tags')
                    this.$watch(
                        () => this.value.length,
                        (newV) => {
                            dom.innerHTML = newV ? `<span class="check_num">${newV}</span>` : ''
                        },
                        {
                            immediate: true,
                        }
                    )
                })
            }
        },
        
        // 选择项变化时emit change事件
        onChange(e) {
            const arr = e
            if (this.multiple) { // 全选状态下有一个被取消选择，就删掉全选。
                const hasAll = arr.findIndex(item => item === this.allLabelOption)
                if (hasAll > -1 && arr.length <= this.options.length) {
                    arr.splice(hasAll, 1)
                }
                this.activeValue = arr

                /* 
                    这里是点击全选option时，会依次执行onChange，handleCheckAll，但只需要执行handleCheckAll，
                    按照事件执行顺序，会是onChange > handleCheckAll > onChange下的setTimeout，
                    如果setTimeout中的checkAll发生了变化，说明点击的是全选option，停止emit，
                    如果没变化，点击的就是普通的option选项，emit选择项。
                */
                let oldCheckAll = this.checkAll
                setTimeout(() => {
                    if (oldCheckAll === this.checkAll) {
                        this.$emit('change', this.activeValue)
                    }
                }, 10)
            } else { // 单选
                this.activeValue = e
                this.$emit('change', this.activeValue)
            }
        },
        
        // 全选
        handleCheckAll() {
            this.checkAll = !this.checkAll
            if (this.checkAll) {
                if (this.valueKey === 'value') {
                    this.activeValue = this.options.map(item => item[this.option.value])
                } else {
                    this.activeValue = [...this.options]
                }
                this.activeValue.unshift(this.allLabelOption)
            } else {
                this.activeValue = []
            }
            this.$emit('change', this.activeValue.filter(item => item !== this.allLabelOption))
        }
    },

    model: {
        prop: 'value',
        event: 'change',
    },
}
</script>

<style lang="scss">
    .check_num {
        display: inline!important;
        margin-left: 12px;
        font-size: 16px;
        line-height: 20px;
        color: $base-font-major;
        font-family: AkrobatMedium;
    }
</style>